/**
 * Client half of the consent evidence write (GitLab #649, D15).
 *
 * `ConsentProvider` reloads the page immediately after a choice that changes
 * which tags are loaded, so this POST has to survive the document being torn
 * down. `keepalive` is what makes that true: without it the request is
 * cancelled on navigation and the evidence for exactly the choices that
 * matter most — first acceptance and withdrawal — is the evidence most often
 * lost.
 *
 * Failure is deliberately silent. The customer's choice is already stored in
 * the cookie and already acted on; surfacing a network error would ask them
 * to do something about a record they cannot see and cannot fix.
 */

import type { ConsentChoiceRecord } from '@/components/analytics/ConsentProvider';

/** Endpoint that writes the append-only `cookie_consent_log` row. */
export const CONSENT_LOG_ENDPOINT = '/api/consent';

/** The `fetch` surface this module needs, so tests can pass a double. */
export type ConsentFetch = (
  input: string,
  init: RequestInit,
) => Promise<unknown>;

export function recordConsentChoice(
  record: ConsentChoiceRecord,
  fetchImpl?: ConsentFetch,
): void {
  // No organisation means no child org resolved for this page, so there is
  // nothing to attribute the row to and the server would reject it anyway.
  if (!record.organizationId) return;

  const send =
    fetchImpl ??
    (typeof fetch === 'function'
      ? ((input, init) => fetch(input, init)) as ConsentFetch
      : null);
  if (!send) return;

  try {
    void Promise.resolve(
      send(CONSENT_LOG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          organizationId: record.organizationId,
          anonymousId: record.anonymousId,
          categories: record.categories,
          consentVersion: record.consentVersion,
          action: record.action,
        }),
      }),
    ).catch(() => {});
  } catch {
    // Ignored: see the module note on silent failure.
  }
}
